import React, {useEffect, useMemo, useState} from 'react';
import T from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import {toast} from 'react-toastify';
import DialogClose from 'components/DialogClose/styles';
import DialogContainer from 'components/DialogContainer/styles';
import ButtonOutline from 'components/buttons/ButtonOutline/styles';
import ButtonPrimary from 'components/buttons/ButtonPrimary/styles';
import SwitchS2E from 'components/Switch';
import Loading from 'components/shared/Utils/Loading';
import {
  CashbackConfirmIntro,
  CashbackConfirmItem,
  CashbackConfirmList,
  CashbackField,
  CashbackFieldsRow,
  CashbackHint,
  CashbackInputWrap,
  CashbackModalActions,
  CashbackModalSubtitle,
  CashbackModalTitle,
  CashbackSectionText,
  CashbackSectionTitle,
  CashbackSettingsBody,
  CashbackSettingsSection,
  CashbackToggleBody,
  RequiredMark
} from 'routes/App/cashback/styles';
import {FormControl} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import SelectWrapper from 'components/select/SelectComponentStyles';
import FieldLabel from 'components/FieldLabel/styles';
import FieldInput from 'components/FieldInput';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import IconHint from 'assets/icon-lightbulb-outline.svg';
import IconWarning from 'assets/icon-cashback-warning.svg';

const OTHER_OPTION = 'other';
const RATE_OPTIONS = [5, 10, 15];
const EXPIRATION_OPTIONS = [60, 90, 120];
const DEFAULT_RATE = 10;
const DEFAULT_EXPIRATION_DAYS = 60;
const RATE_ERROR = 'Enter a value between 1% and 25%.';
const EXPIRATION_ERROR = 'Enter a value between 30 and 365 days.';
const STATUS_ENABLED = 'enabled';
const STATUS_DISABLED = 'disabled';

const captureCashbackEvent = (eventName, properties) => {
  if (window?.posthog) {
    window.posthog.capture(eventName, properties);
  }
};

const getCampaignStatus = (settings) =>
  (settings?.status || STATUS_ENABLED).toLowerCase();

const getCampaignEnabled = (settings) => {
  if (!settings?.id) {
    return false;
  }

  return getCampaignStatus(settings) !== STATUS_DISABLED;
};

const getSettingRate = (settings) => {
  const rate = settings?.rates?.find((item) => item?.type === 'percentage');
  const value = Number(rate?.value ?? settings?.rates?.[0]?.value);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_RATE;
};

const getSettingExpiration = (settings) => {
  const value = Number(settings?.expirationDays);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_EXPIRATION_DAYS;
};

const buildFormState = (settings) => {
  const rate = getSettingRate(settings);
  const expirationDays = getSettingExpiration(settings);

  return {
    rateOption: RATE_OPTIONS.includes(rate) ? rate : OTHER_OPTION,
    rateValue: String(rate),
    expirationOption: EXPIRATION_OPTIONS.includes(expirationDays)
      ? expirationDays
      : OTHER_OPTION,
    expirationValue: String(expirationDays),
    enabled: getCampaignEnabled(settings)
  };
};

const isInRange = (value, min, max) => {
  const numberValue = Number(value);
  return (
    value !== '' &&
    Number.isFinite(numberValue) &&
    numberValue >= min &&
    numberValue <= max
  );
};

const CashbackSettingsModal = ({
  open,
  mode,
  settings,
  settingsLoading,
  locationId,
  outstandingCreditAmount,
  onClose,
  onSaveSettings
}) => {
  const [form, setForm] = useState(buildFormState(settings));
  const [initialForm, setInitialForm] = useState(buildFormState(settings));
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const nextForm = buildFormState(settings);
      setForm(nextForm);
      setInitialForm(nextForm);
      setSaving(false);
      setConfirmOpen(false);
    }
  }, [open, settings]);

  const showToggleOnly = mode === 'turnOff';
  const isTurnOnSettings = !showToggleOnly && !initialForm.enabled;

  const currentRate = useMemo(
    () =>
      Number(
        form.rateOption === OTHER_OPTION ? form.rateValue : form.rateOption
      ),
    [form.rateOption, form.rateValue]
  );
  const initialRate = useMemo(
    () =>
      Number(
        initialForm.rateOption === OTHER_OPTION
          ? initialForm.rateValue
          : initialForm.rateOption
      ),
    [initialForm.rateOption, initialForm.rateValue]
  );
  const currentExpirationDays = useMemo(
    () =>
      Number(
        form.expirationOption === OTHER_OPTION
          ? form.expirationValue
          : form.expirationOption
      ),
    [form.expirationOption, form.expirationValue]
  );
  const initialExpirationDays = useMemo(
    () =>
      Number(
        initialForm.expirationOption === OTHER_OPTION
          ? initialForm.expirationValue
          : initialForm.expirationOption
      ),
    [initialForm.expirationOption, initialForm.expirationValue]
  );

  const errors = useMemo(() => {
    const nextErrors = {};

    if (form.rateOption === OTHER_OPTION && !isInRange(form.rateValue, 1, 25)) {
      nextErrors.rate = RATE_ERROR;
    }

    if (
      form.expirationOption === OTHER_OPTION &&
      !isInRange(form.expirationValue, 30, 365)
    ) {
      nextErrors.expiration = EXPIRATION_ERROR;
    }

    return nextErrors;
  }, [
    form.expirationOption,
    form.expirationValue,
    form.rateOption,
    form.rateValue
  ]);

  const hasSettingsChanges =
    currentRate !== initialRate ||
    currentExpirationDays !== initialExpirationDays;
  const hasToggleChange = form.enabled !== initialForm.enabled;
  const hasChanges = showToggleOnly
    ? hasToggleChange
    : isTurnOnSettings || hasSettingsChanges;
  const isSaveDisabled =
    saving || settingsLoading || Object.keys(errors).length > 0 || !hasChanges;

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleRateOptionChange = (event) => {
    const {value} = event.target;
    setForm((prev) => ({
      ...prev,
      rateOption: value,
      rateValue: value === OTHER_OPTION ? prev.rateValue : value
    }));
  };

  const handleExpirationOptionChange = (event) => {
    const {value} = event.target;
    setForm((prev) => ({
      ...prev,
      expirationOption: value,
      expirationValue: value === OTHER_OPTION ? prev.expirationValue : value
    }));
  };

  const buildPayload = (status) => ({
    rates: [{type: 'percentage', value: currentRate}],
    status,
    expirationDays: currentExpirationDays
  });

  const saveSettings = (status) => {
    if (!settings?.id && status === STATUS_DISABLED) {
      toast.error('Cashback settings are still loading. Please try again.');
      return;
    }

    setSaving(true);

    const data = settings?.id
      ? buildPayload(status)
      : {
          locationIds: [locationId],
          rates: [{type: 'percentage', value: currentRate}],
          expirationDays: currentExpirationDays
        };

    onSaveSettings({
      settingsId: settings?.id,
      data,
      onSuccess: () => {
        if (status === STATUS_DISABLED) {
          captureCashbackEvent('cashback_turned_off', {
            location_id: locationId,
            current_rate: initialRate,
            current_expiration_days: initialExpirationDays,
            outstanding_credit_amount: outstandingCreditAmount || 0
          });
          toast.success(
            'Cashback turned off. Existing credits remain redeemable until they expire.'
          );
        } else {
          if (!showToggleOnly && currentRate !== initialRate) {
            captureCashbackEvent('cashback_rate_changed', {
              location_id: locationId,
              old_rate: initialRate,
              new_rate: currentRate
            });
          }

          if (
            !showToggleOnly &&
            currentExpirationDays !== initialExpirationDays
          ) {
            captureCashbackEvent('cashback_expiration_changed', {
              location_id: locationId,
              old_days: initialExpirationDays,
              new_days: currentExpirationDays
            });
          }

          toast.success(
            showToggleOnly
              ? 'Cashback turned on.'
              : 'Cashback settings updated.'
          );
        }

        setConfirmOpen(false);
        onClose();
        setSaving(false);
      },
      onError: () => {
        toast.error('Failed to update cashback settings');
        setSaving(false);
      }
    });
  };

  const handleSave = () => {
    if (isSaveDisabled) {
      return;
    }

    if (showToggleOnly) {
      if (initialForm.enabled && !form.enabled) {
        setConfirmOpen(true);
        return;
      }

      if (!initialForm.enabled && form.enabled) {
        saveSettings(STATUS_ENABLED);
      }
      return;
    }

    saveSettings(STATUS_ENABLED);
  };

  const renderSettingsFields = () => (
    <>
      <CashbackSettingsSection>
        <CashbackSectionTitle>
          Cashback rate<RequiredMark>*</RequiredMark>
        </CashbackSectionTitle>
        <CashbackSectionText>
          The percentage of each order amount your customers earn as credit.
          They can use this credit on future orders at your location.
        </CashbackSectionText>
        <CashbackFieldsRow>
          <SelectWrapper className='SelectMd w100'>
            <FieldLabel className='mBottomMd FieldLabelLg'>
              Cashback rate
            </FieldLabel>
            <FormControl className='select-formcontrol'>
              <Select
                value={form.rateOption}
                name='rateOption'
                onChange={handleRateOptionChange}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  getContentAnchorEl: null
                }}
              >
                <MenuItem value={5}>5%</MenuItem>
                <MenuItem value={10}>10%</MenuItem>
                <MenuItem value={15}>15%</MenuItem>
                <MenuItem value={OTHER_OPTION}>Other</MenuItem>
              </Select>
            </FormControl>
          </SelectWrapper>
          {form.rateOption === OTHER_OPTION && (
            <CashbackField>
              <FieldLabel className='mBottomMd FieldLabelLg'>
                Cashback %
              </FieldLabel>
              <CashbackInputWrap className={errors.rate ? 'error' : ''}>
                <FieldInput
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      rateValue: event.target.value
                    }))
                  }
                  name='rateValue'
                  type='number'
                  value={form.rateValue}
                />
              </CashbackInputWrap>
              {errors.rate && (
                <ErrorSignWrapper
                  className='noWrap'
                  topPosition='4px'
                  leftPosition='0'
                >
                  <ErrorSign>{errors.rate}</ErrorSign>
                </ErrorSignWrapper>
              )}
            </CashbackField>
          )}
        </CashbackFieldsRow>
      </CashbackSettingsSection>

      <CashbackSettingsSection>
        <CashbackSectionTitle>
          Credit expiration<RequiredMark>*</RequiredMark>
        </CashbackSectionTitle>
        <CashbackSectionText>
          How long customers have to use their cashback credit after they earn
          it. After this period, unused credit expires and is no longer
          redeemable.
        </CashbackSectionText>
        <CashbackFieldsRow>
          <SelectWrapper className='SelectMd w100'>
            <FieldLabel className='mBottomMd FieldLabelLg'>
              Credit expiration
            </FieldLabel>
            <FormControl className='select-formcontrol'>
              <Select
                value={form.expirationOption}
                name='expirationOption'
                onChange={handleExpirationOptionChange}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  },
                  getContentAnchorEl: null
                }}
              >
                <MenuItem value={60}>60 days</MenuItem>
                <MenuItem value={90}>90 days</MenuItem>
                <MenuItem value={120}>120 days</MenuItem>
                <MenuItem value={OTHER_OPTION}>Other</MenuItem>
              </Select>
            </FormControl>
          </SelectWrapper>
          {form.expirationOption === OTHER_OPTION && (
            <CashbackField>
              <FieldLabel className='mBottomMd FieldLabelLg'>Days</FieldLabel>
              <CashbackInputWrap className={errors.expiration ? 'error' : ''}>
                <FieldInput
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      expirationValue: event.target.value
                    }))
                  }
                  name='expirationValue'
                  type='number'
                  value={form.expirationValue}
                />
              </CashbackInputWrap>
              {errors.expiration && (
                <ErrorSignWrapper
                  className='noWrap'
                  topPosition='4px'
                  leftPosition='0'
                >
                  <ErrorSign>{errors.expiration}</ErrorSign>
                </ErrorSignWrapper>
              )}
            </CashbackField>
          )}
        </CashbackFieldsRow>
      </CashbackSettingsSection>

      <CashbackHint>
        <img className='icon' src={IconHint} alt='hint' />
        <span className='text'>
          Changes apply only to new credits earned from now on - existing
          customer credits keep their original expiration.
        </span>
      </CashbackHint>
    </>
  );

  const renderToggleOnly = () => (
    <CashbackToggleBody>
      <CashbackSectionTitle>Turn off cashback</CashbackSectionTitle>
      <CashbackSectionText>
        Stop the cashback program at this location. Existing credits are not
        erased - review the implications before turning off
      </CashbackSectionText>
      <div className='ToggleRow'>
        <SwitchS2E
          name='cashbackEnabled'
          checked={form.enabled}
          className='red'
          labelPlacement='start'
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              enabled: event.target.checked
            }))
          }
          label='Turn on cashback'
        />
      </div>
    </CashbackToggleBody>
  );

  const primaryButtonLabel = (() => {
    if (saving) {
      return 'Saving...';
    }

    return isTurnOnSettings ? 'Turn on cashback' : 'Save changes';
  })();

  return (
    <>
      <Dialog open={open} onClose={handleClose} scroll='body'>
        <DialogContainer className='DialogM'>
          <DialogClose onClick={handleClose} />
          <CashbackModalTitle>
            {isTurnOnSettings ? 'Turn on cashback' : 'Cashback settings'}
          </CashbackModalTitle>
          <CashbackModalSubtitle>
            Configure how cashback works at this location
          </CashbackModalSubtitle>

          {settingsLoading ? (
            <Loading margin='48px 0' />
          ) : (
            <CashbackSettingsBody>
              {showToggleOnly ? renderToggleOnly() : renderSettingsFields()}
            </CashbackSettingsBody>
          )}

          <CashbackModalActions>
            <ButtonOutline onClick={handleClose}>
              <span>Cancel</span>
            </ButtonOutline>
            <ButtonPrimary
              onClick={handleSave}
              disabled={isSaveDisabled}
              className={isSaveDisabled ? 'disabled' : ''}
            >
              <span>{primaryButtonLabel}</span>
            </ButtonPrimary>
          </CashbackModalActions>
        </DialogContainer>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        scroll='body'
      >
        <DialogContainer className='DialogM'>
          <DialogClose onClick={() => setConfirmOpen(false)} />
          <CashbackModalTitle>Turn off cashback?</CashbackModalTitle>
          <CashbackConfirmIntro>
            Restaurants using cashback see customers come back{' '}
            <strong>2.5x more often.</strong> Before you turn it off,
            here&apos;s what will happen:
          </CashbackConfirmIntro>
          <CashbackConfirmList>
            <CashbackConfirmItem>
              <img src={IconWarning} alt='warning' className='mark' />
              <span className='copy'>
                <strong>Cashback is removed from your storefront</strong> -
                customers won&apos;t see or earn new credit.
              </span>
            </CashbackConfirmItem>
            <CashbackConfirmItem>
              <img src={IconWarning} alt='warning' className='mark' />
              <span className='copy'>
                <strong>Existing customer credits stay valid</strong> -
                customers with unused credit can still redeem it until their
                credit expires.
              </span>
            </CashbackConfirmItem>
            <CashbackConfirmItem>
              <img src={IconWarning} alt='warning' className='mark' />
              <span className='copy'>
                <strong>No new credit will be issued</strong> - once expired,
                all credit is gone.
              </span>
            </CashbackConfirmItem>
            <CashbackConfirmItem>
              <img src={IconWarning} alt='warning' className='mark' />
              <span className='copy'>
                <strong>You can turn cashback back on anytime</strong> - but
                credit that expired in the off period won&apos;t come back.
              </span>
            </CashbackConfirmItem>
          </CashbackConfirmList>
          <CashbackModalActions>
            <ButtonOutline onClick={() => setConfirmOpen(false)}>
              <span>Cancel</span>
            </ButtonOutline>
            <ButtonPrimary
              onClick={() => saveSettings(STATUS_DISABLED)}
              disabled={saving}
              className={saving ? 'disabled' : ''}
            >
              <span>{saving ? 'Saving...' : 'Yes, turn off cashback'}</span>
            </ButtonPrimary>
          </CashbackModalActions>
        </DialogContainer>
      </Dialog>
    </>
  );
};

CashbackSettingsModal.propTypes = {
  open: T.bool.isRequired,
  mode: T.oneOf(['settings', 'turnOff']),
  settings: T.object,
  settingsLoading: T.bool,
  locationId: T.string,
  outstandingCreditAmount: T.number,
  onClose: T.func.isRequired,
  onSaveSettings: T.func.isRequired
};

CashbackSettingsModal.defaultProps = {
  mode: 'settings',
  settings: null,
  settingsLoading: false,
  locationId: '',
  outstandingCreditAmount: 0
};

export default CashbackSettingsModal;
